
import React, { useState, useEffect } from 'react';
import { ExerciseType, LeaderboardEntry } from '../types';
import { EXERCISE_CONFIG } from '../constants';
import { subscribeToLeaderboard } from '../services/database';
import { Card } from '../components/Card';

export const Leaderboard: React.FC = () => {
  const [exercise, setExercise] = useState<ExerciseType>(ExerciseType.PUSHUP);
  const [duration, setDuration] = useState<number>(120); // Default 2 mins
  const [entries, setEntries] = useState<LeaderboardEntry[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    const unsubscribe = subscribeToLeaderboard(exercise, duration, (data) => {
      setEntries(data);
      setLoading(false);
    });
    return () => unsubscribe();
  }, [exercise, duration]);

  return (
    <div className="max-w-5xl mx-auto py-8 px-4 pb-24">
      <div className="mb-8">
        <h1 className="text-3xl font-black text-white uppercase tracking-tighter">Global Ranking</h1>
        <p className="text-secondary font-mono text-xs uppercase tracking-widest">Elite Operators</p>
      </div>

      {/* Filters */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8">
        <div>
          <label className="block text-[10px] text-slate-500 font-mono uppercase mb-2">Protocol</label>
          <div className="flex flex-wrap gap-2">
            {Object.keys(EXERCISE_CONFIG).map((type) => (
              <button
                key={type}
                onClick={() => setExercise(type as ExerciseType)}
                className={`px-4 py-2 text-xs font-bold uppercase tracking-wider border transition-all ${
                  exercise === type 
                    ? 'bg-white text-black border-white' 
                    : 'bg-transparent text-slate-400 border-white/20 hover:border-white hover:text-white'
                }`}
              >
                {EXERCISE_CONFIG[type as ExerciseType].name}
              </button>
            ))}
          </div>
        </div>
        <div>
          <label className="block text-[10px] text-slate-500 font-mono uppercase mb-2">Duration</label>
          <div className="flex gap-2">
             <button
                onClick={() => setDuration(120)}
                className={`flex-1 px-4 py-2 text-xs font-bold uppercase tracking-wider border transition-all ${
                  duration === 120 
                    ? 'bg-amber-500 text-black border-amber-500' 
                    : 'bg-transparent text-slate-400 border-white/20 hover:border-amber-500 hover:text-amber-500'
                }`}
              >
                2 Minutes
              </button>
              <button
                onClick={() => setDuration(240)}
                className={`flex-1 px-4 py-2 text-xs font-bold uppercase tracking-wider border transition-all ${
                  duration === 240 
                    ? 'bg-red-600 text-white border-red-600' 
                    : 'bg-transparent text-slate-400 border-white/20 hover:border-red-600 hover:text-red-600'
                }`}
              >
                4 Minutes
              </button>
          </div>
        </div>
      </div>

      {/* Table */}
      {loading ? (
        <div className="text-center py-20">
          <div className="inline-block w-8 h-8 border-t-2 border-white rounded-full animate-spin"></div>
        </div>
      ) : entries.length === 0 ? (
        <Card className="text-center py-12 border-dashed">
          <p className="text-slate-500 font-mono text-sm uppercase">No records established yet.</p>
        </Card>
      ) : (
        <div className="space-y-2">
          {/* Header */}
          <div className="flex px-4 py-2 text-[10px] text-slate-500 font-mono uppercase tracking-widest">
            <div className="w-12">Rank</div>
            <div className="flex-1">Operator</div>
            <div className="w-20 text-right">Acc %</div>
            <div className="w-24 text-right">Reps</div>
          </div>

          {entries.map((entry, index) => (
            <div 
              key={`${entry.userId}_${index}`} 
              className={`flex items-center px-4 py-4 bg-surface border border-white/5 hover:border-white/20 transition-colors ${index === 0 ? 'border-amber-500/50 bg-amber-900/10' : ''}`}
            >
              <div className={`w-12 font-black text-xl italic ${
                index === 0 ? 'text-amber-500' : 
                index === 1 ? 'text-slate-300' : 
                index === 2 ? 'text-orange-700' : 'text-slate-600'
              }`}>
                #{index + 1}
              </div>
              <div className="flex-1 flex items-center gap-3">
                {entry.photoURL ? (
                  <img src={entry.photoURL} alt="" className="w-8 h-8 rounded-full border border-white/10" />
                ) : (
                  <div className="w-8 h-8 bg-slate-800 rounded-full flex items-center justify-center text-xs font-bold">
                    {entry.nickname.charAt(0)}
                  </div>
                )}
                <div>
                  <p className="text-white font-bold uppercase tracking-wide text-sm">{entry.nickname}</p>
                  <p className="text-[10px] text-slate-500 font-mono">{new Date(entry.date).toLocaleDateString()}</p>
                </div>
              </div>
              <div className="w-20 text-right font-mono text-slate-400 text-sm">
                {entry.accuracy}%
              </div>
              <div className="w-24 text-right font-black text-white text-2xl">
                {entry.reps}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};